import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Terminal, Shield, FolderGit2, BookHeart, Layers, LogIn, LogOut, UserCircle2, ShieldCheck, ChevronDown, Sparkles, Lock, Activity, UserCheck, Newspaper, CloudSun, Plane, Database } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

export const Header = ({ currentTab, setTab, onOpenAuth }) => {
  const { isAuthenticated, isAdmin, user, logout } = useAuth();
  const [featuresOpen, setFeaturesOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const [dataOpen, setDataOpen] = useState(false);
  const featuresRef = useRef(null);
  const adminRef = useRef(null);
  const dataRef = useRef(null);
  const mobileNavRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      const t = e.target;
      if (mobileNavRef.current && mobileNavRef.current.contains(t)) return;
      if (featuresRef.current && !featuresRef.current.contains(t)) setFeaturesOpen(false);
      if (adminRef.current && !adminRef.current.contains(t)) setAdminOpen(false);
      if (dataRef.current && !dataRef.current.contains(t)) setDataOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const baseTabs = [
    { id: 'home', label: '01 HOME', icon: Terminal },
    { id: 'achievements', label: '02 PROFILE', icon: FolderGit2 },
    { id: 'useful_data', label: '03 USEFUL DATA', icon: Database },
    { id: 'guestbook', label: '04 GUEST', icon: BookHeart },
  ];
  const featuresTab = { id: 'features', label: '05 FEATURES', icon: Layers };
  const adminTab = { id: 'admin', label: '06 ADMIN', icon: ShieldCheck };
  let tabs = isAuthenticated ? [...baseTabs, featuresTab] : baseTabs;
  if (isAdmin) tabs = [...tabs, adminTab];

  const featureItems = [
    { id: 'prompt_analyzer', label: 'Prompt Analyzer', icon: Sparkles, available: true },
    { id: 'stocks', label: 'Stocks Watchlist', icon: Lock, available: false, hint: 'Coming soon' },
  ];

  const usefulDataItems = [
    { id: 'tech_feeds', label: 'News', icon: Newspaper, available: true },
    { id: 'weather', label: 'Weather', icon: CloudSun, available: true },
    {
      id: 'flights',
      label: 'Flights',
      icon: isAuthenticated ? Plane : Lock,
      available: isAuthenticated,
      hint: isAuthenticated ? undefined : 'Login required',
    },
  ];

  const adminItems = [
    { id: 'admin', label: 'Admin Telemetry', icon: Activity },
    { id: 'admin_approvals', label: 'Registration Approval', icon: UserCheck },
  ];

  const PARENT_MAP = {
    features: { open: featuresOpen, setOpen: setFeaturesOpen, ref: featuresRef, items: featureItems, childIds: ['prompt_analyzer', 'stocks'] },
    admin: { open: adminOpen, setOpen: setAdminOpen, ref: adminRef, items: adminItems, childIds: ['admin_approvals'] },
    useful_data: { open: dataOpen, setOpen: setDataOpen, ref: dataRef, items: usefulDataItems, childIds: ['tech_feeds', 'weather', 'flights'] },
  };

  return (
    <header className="sticky top-0 z-40 w-full border-b border-slate-800/80 bg-slate-950/80 backdrop-blur-md px-4 py-3 md:px-6">
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-3">
        <div
          onClick={() => setTab('home')}
          className="flex items-center gap-2.5 cursor-pointer group"
          data-testid="header-logo"
        >
          <div className="relative w-9 h-9 rounded bg-gradient-to-br from-[#00f0ff] to-[#9d4edd] p-[1px] flex items-center justify-center shadow-lg transition-transform duration-300 group-hover:scale-105">
            <div className="w-full h-full bg-slate-950 rounded-[3px] flex items-center justify-center font-display font-black text-[11px] text-[#00f0ff] tracking-tighter">
              RS
            </div>
            <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-[#39ff14] signal-dot" />
          </div>
          <div>
            <span className="font-display font-extrabold text-[#f1f5f9] tracking-wider text-sm">RISHAB SALGOTRA</span>
            <div className="font-mono text-[9px] text-[#00f0ff] uppercase tracking-widest leading-none">
              BUSINESS LEADER · TECHNOLOGY EXPERT
            </div>
          </div>
        </div>

        <nav className="hidden md:flex items-center gap-1.5 bg-slate-900/50 p-1 rounded-lg border border-slate-800/80">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const parent = PARENT_MAP[tab.id];
            const isParent = !!parent;
            const isActive = currentTab === tab.id || (parent && parent.childIds.includes(currentTab));

            if (isParent) {
              const { open, setOpen, ref: dropRef, items } = parent;
              return (
                <div key={tab.id} className="relative" ref={dropRef}>
                  <button
                    onClick={() => setOpen((o) => !o)}
                    className={`relative px-4 py-2 font-mono text-xs font-semibold rounded flex items-center gap-2 transition-all duration-300 cursor-pointer ${isActive ? 'text-[#00f0ff]' : 'text-slate-400 hover:text-slate-200'}`}
                    data-testid={`nav-${tab.id}`}
                  >
                    {isActive && (
                      <motion.div layoutId="active-tab-glow" className="absolute inset-0 bg-[#00f0ff]/5 border border-[#00f0ff]/30 rounded" transition={{ type: 'spring', stiffness: 380, damping: 30 }} />
                    )}
                    <Icon className="w-3.5 h-3.5 relative" />
                    <span className="relative">{tab.label}</span>
                    <ChevronDown className={`w-3 h-3 relative transition-transform ${open ? 'rotate-180' : ''}`} />
                  </button>
                  {open && (
                    <div className="absolute top-full right-0 mt-2 min-w-[230px] rounded-lg border border-slate-800 bg-slate-950/95 backdrop-blur-md shadow-xl z-50 overflow-hidden" data-testid={`${tab.id}-dropdown`}>
                      {items.map((it) => {
                        const ItIcon = it.icon;
                        const disabled = it.available === false;
                        return (
                          <button
                            key={it.id}
                            onClick={() => {
                              if (disabled) return;
                              setTab(it.id);
                              setOpen(false);
                            }}
                            disabled={disabled}
                            className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-left font-mono text-[11px] transition-all ${disabled ? 'text-slate-500 cursor-not-allowed' : 'text-slate-200 hover:bg-[#00f0ff]/10 hover:text-[#00f0ff] cursor-pointer'} ${currentTab === it.id ? 'bg-[#00f0ff]/10 text-[#00f0ff]' : ''}`}
                            data-testid={`${tab.id}-item-${it.id}`}
                          >
                            <ItIcon className="w-3.5 h-3.5" />
                            <span className="flex-1">{it.label}</span>
                            {disabled && <span className="font-mono text-[8px] text-amber-400 uppercase">{it.hint}</span>}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }
            return (
              <button
                key={tab.id}
                onClick={() => setTab(tab.id)}
                className={`relative px-4 py-2 font-mono text-xs font-semibold rounded flex items-center gap-2 transition-all duration-300 cursor-pointer ${isActive ? 'text-[#00f0ff]' : 'text-slate-400 hover:text-slate-200'}`}
                data-testid={`nav-${tab.id}`}
              >
                {isActive && (
                  <motion.div layoutId="active-tab-glow" className="absolute inset-0 bg-[#00f0ff]/5 border border-[#00f0ff]/30 rounded" transition={{ type: 'spring', stiffness: 380, damping: 30 }} />
                )}
                <Icon className="w-3.5 h-3.5 relative" />
                <span className="relative">{tab.label}</span>
              </button>
            );
          })}
        </nav>

        <div className="flex items-center gap-2">
          {isAuthenticated ? (
            <>
              <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded border border-slate-800 bg-slate-900/60" data-testid="user-chip">
                <UserCircle2 className="w-3.5 h-3.5 text-[#39ff14]" />
                <span className="font-mono text-[10px] text-slate-300 max-w-[110px] truncate">{user?.full_name || user?.email}</span>
              </div>
              <button
                onClick={logout}
                className="px-3 py-1.5 rounded font-mono font-bold text-[10px] tracking-wider bg-rose-500/10 border border-rose-500/30 text-rose-300 hover:bg-rose-500 hover:text-white transition-all flex items-center gap-1.5 cursor-pointer"
                data-testid="btn-logout"
              >
                <LogOut className="w-3 h-3" />
                <span>LOGOUT</span>
              </button>
            </>
          ) : (
            <button
              onClick={onOpenAuth}
              className="px-4 py-1.5 rounded font-display font-bold text-xs tracking-wide bg-gradient-to-r from-[#00f0ff] to-[#0070ff] hover:from-[#0070ff] hover:to-[#00f0ff] text-slate-950 hover:scale-[1.02] active:scale-[0.98] transition-all shadow-md cursor-pointer flex items-center gap-1.5"
              data-testid="btn-login-header"
            >
              <LogIn className="w-3 h-3" />
              <span>LOGIN</span>
            </button>
          )}
        </div>
      </div>

      <div ref={mobileNavRef} className="md:hidden mt-3 pt-3 border-t border-slate-900/60">
        <div className="flex items-center justify-around gap-1 overflow-x-auto">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const parent = PARENT_MAP[tab.id];
            const isParent = !!parent;
            const isActive = currentTab === tab.id || (parent && parent.childIds.includes(currentTab));
            const isOpen = parent && parent.open;
            return (
              <button
                key={tab.id}
                onClick={() => {
                  if (isParent) {
                    parent.setOpen(!parent.open);
                    // close siblings
                    Object.entries(PARENT_MAP).forEach(([k, v]) => { if (k !== tab.id) v.setOpen(false); });
                    return;
                  }
                  Object.values(PARENT_MAP).forEach((v) => v.setOpen(false));
                  setTab(tab.id);
                }}
                className={`flex-1 flex flex-col items-center gap-1 py-1.5 rounded transition-all ${isActive ? 'text-[#00f0ff]' : 'text-slate-500 hover:text-slate-300'}`}
                data-testid={`nav-mobile-${tab.id}`}
              >
                <div className="flex items-center gap-0.5">
                  <Icon className="w-4 h-4" />
                  {isParent && (
                    <ChevronDown className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                  )}
                </div>
                <span className="font-mono text-[8px] tracking-wider uppercase">{tab.label.split(' ').slice(1).join(' ')}</span>
              </button>
            );
          })}
        </div>
        {(featuresOpen || adminOpen || dataOpen) && (
          <div className="mt-2 rounded-lg border border-slate-800 bg-slate-950/90 backdrop-blur overflow-hidden divide-y divide-slate-900" data-testid="mobile-submenu">
            {(featuresOpen ? featureItems : adminOpen ? adminItems : usefulDataItems).map((it) => {
              const ItIcon = it.icon;
              const disabled = it.available === false;
              return (
                <button
                  key={it.id}
                  onClick={() => {
                    if (disabled) return;
                    setTab(it.id);
                    Object.values(PARENT_MAP).forEach((v) => v.setOpen(false));
                  }}
                  disabled={disabled}
                  className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-left font-mono text-[11px] transition-all ${disabled ? 'text-slate-500 cursor-not-allowed' : 'text-slate-200 hover:bg-[#00f0ff]/10 hover:text-[#00f0ff] cursor-pointer'} ${currentTab === it.id ? 'bg-[#00f0ff]/10 text-[#00f0ff]' : ''}`}
                  data-testid={`mobile-submenu-item-${it.id}`}
                >
                  <ItIcon className="w-3.5 h-3.5" />
                  <span className="flex-1">{it.label}</span>
                  {disabled && <span className="font-mono text-[8px] text-amber-400 uppercase">{it.hint}</span>}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </header>
  );
};
